import { Inject, LOCALE_ID, Pipe, PipeTransform } from '@angular/core';
import { DatePipe } from '@angular/common';

/**
 * Drop-in, crash-proof replacement for Angular's built-in `date` pipe.
 *
 * The built-in `DatePipe` throws `InvalidPipeArgument` (Angular error NG02100)
 * whenever it receives a value it cannot parse — e.g. an `Invalid Date`, a
 * time-only string ("09:00"), or a non-ISO datetime that Safari/iOS refuses to
 * parse even though Chrome accepts it. A single bad value crashes the whole
 * view and floods production error logs. We have been bitten by this repeatedly
 * (order receipt, contact list, and now the scheduling module).
 *
 * `safeDate` delegates formatting to `DatePipe` (so output is byte-for-byte
 * identical for valid inputs) but returns '' for null/empty/invalid values
 * instead of throwing. Standardise date rendering on this pipe so NG02100 from
 * dates cannot recur.
 *
 * Usage is identical to `date`:  {{ value | safeDate:'h:mm a' }}
 */
@Pipe({
  name: 'safeDate',
  standalone: true,
})
export class SafeDatePipe implements PipeTransform {
  private readonly datePipe: DatePipe;

  constructor(@Inject(LOCALE_ID) locale: string) {
    this.datePipe = new DatePipe(locale);
  }

  transform(
    value: string | number | Date | null | undefined,
    format = 'mediumDate',
    timezone?: string,
    locale?: string,
  ): string {
    if (value === null || value === undefined || value === '') {
      return '';
    }
    try {
      return this.datePipe.transform(value, format, timezone, locale) ?? '';
    } catch {
      // Invalid/unparseable value — render blank rather than throwing NG02100.
      return '';
    }
  }
}
